import { Card, Divider, Stack, Typography } from "@mui/material";
import { addMinutes, differenceInMinutes, isSameDay } from "date-fns";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import UserMedspaInitialsAvatar from "@/components/common/avatar/userMedspaInitialsAvatar";
import { Provider } from "@/components/common/providerSection";
import { GREY } from "@/config/mui/colorPalette";
import useIsTimeCorrect from "@/hooks/misc/useIsTimeCorrect";
import { constrainToOneFullDay } from "@/utils/date";
import { AvailableService } from "./appointmentServicesSection";
import { SmallTimeSelector } from "./smallTimeSelector";

type Props = {
  provider: Provider;
  serviceMenuItem: AvailableService;
  startTime: Date;
  endTime: Date;
  minStartTime: Date | null;
  onStartTimeChange: (startTime: Date) => void;
  onEndTimeChange: (endTime: Date) => void;
};

export const AppointmentServiceTimeSelector = ({
  provider,
  serviceMenuItem,
  startTime,
  endTime,
  minStartTime,
  onStartTimeChange,
  onEndTimeChange,
}: Props) => {
  const handleEndDateChange = (date: Date) => {
    const constrainedDate = constrainToOneFullDay(startTime, date);
    onEndTimeChange(constrainedDate);
  };
  const isTimeCorrect = useIsTimeCorrect(
    startTime,
    endTime,
    serviceMenuItem.durationInMinutes
  );
  const actualDuration = differenceInMinutes(endTime, startTime);
  const isDurationWarning = !isTimeCorrect && actualDuration > 0;

  return (
    <Card
      sx={{
        background: GREY[5],
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 1,
      }}
    >
      <Stack gap={1.5} direction="row">
        <UserMedspaInitialsAvatar
          userMedspaId={provider.id}
          initials={provider.user}
          size={24}
          sx={{
            typography: "labelTiny",
          }}
        />
        <Typography variant="subtitleDefault" color="text.primary">
          {serviceMenuItem.name}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1} justifyContent="space-between">
        <SmallTimeSelector
          value={startTime}
          label="Start time"
          onTimeChange={onStartTimeChange}
          minTime={minStartTime}
        />
        <Divider
          orientation="horizontal"
          sx={{
            borderBottomWidth: "2px",
            alignSelf: "center",
            width: ".5rem",
          }}
        />
        <SmallTimeSelector
          value={endTime}
          label="End time"
          onTimeChange={handleEndDateChange}
          minTime={
            isSameDay(startTime, endTime) ? addMinutes(startTime, 1) : undefined
          }
        />
      </Stack>
      {isDurationWarning && (
        <AdditionalInfo
          variant="warning"
          text={`This appointment service is booked for ${actualDuration} minutes, but the selected service typically requires ${serviceMenuItem.durationInMinutes}. You can proceed if this was intentional.`}
          sx={{ mt: 0 }}
        />
      )}
    </Card>
  );
};
